/**
 * Login — email + password contra POST /auth/login.
 *
 * - Si vienes de una ruta protegida, location.state.from guarda dónde
 *   volver tras login exitoso. Si no, default a "/".
 * - Si vienes de /reset-password/confirm tras éxito, location.state.successMessage
 *   muestra el mensaje "Hecho. Entra con tu nueva contraseña."
 * - Validación frontend antes de llamar al API. Si pasa, muestra error general
 *   con el mensaje del backend traducido.
 */
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AppleSignInButton } from "@/components/auth/AppleSignInButton";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { AuthLayout } from "@/components/ui/AuthLayout";
import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { t, translateError } from "@/i18n/es";
import { validateEmail, validatePassword } from "@/lib/validators";
import { ApiError } from "@/types/api";

interface LocationState {
  from?: { pathname: string };
  successMessage?: string;
  /** Sprint 8 S8.4 (UX-008): mensaje informativo enviado desde
   *  /register cuando el flow OAuth Google se interrumpe (token
   *  expirado, F5 con state perdido). Se mapea a copy del namespace
   *  `t.auth.<flash>` y se muestra como banner neutro arriba. */
  flash?: "oauthSessionExpired" | "oauthRestart";
}

export function Login() {
  const { login, googleSignIn, appleSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErr, setEmailErr] = useState<string | undefined>();
  const [passErr, setPassErr] = useState<string | undefined>();
  const [formErr, setFormErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Mensaje informativo en banner: o el éxito de reset-password, o un
  // flash de OAuth interrumpido enviado desde /register (Sprint 8 S8.4).
  const flashMessage =
    state.flash === "oauthSessionExpired"
      ? t.auth.oauthSessionExpired
      : state.flash === "oauthRestart"
        ? t.auth.oauthRestart
        : null;
  const successMessage = state.successMessage ?? flashMessage;
  const redirectTo = state.from?.pathname ?? "/";

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormErr(null);

    const emailErrCode = validateEmail(email);
    const passErrCode = validatePassword(password);
    setEmailErr(emailErrCode ? translateError(emailErrCode.code) : undefined);
    setPassErr(passErrCode ? translateError(passErrCode.code) : undefined);
    if (emailErrCode || passErrCode) return;

    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "network_error";
      setFormErr(translateError(code));
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogleIdToken(idToken: string) {
    setFormErr(null);
    setSubmitting(true);
    try {
      await googleSignIn(idToken);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Sprint 8 S8.4 (UX-008): si el backend marca oauth_pending,
      // el user es NUEVO y necesita meter un invite_code. Redirigimos
      // a /register preservando idToken + email del Google profile
      // canónico para que el form se prefile sin re-OAuth.
      // Sprint 8 S8.4 + S14.3: cualquier respuesta oauth_pending (invite
      // requerido o consentimiento pendiente) → redirigir a /register al
      // flujo "completar registro", preservando el id_token (sin re-OAuth).
      if (err instanceof ApiError && err.oauthPending) {
        navigate("/register", {
          state: {
            oauthIdToken: idToken,
            oauthEmail: err.oauthEmail,
            oauthName: err.oauthName,
            oauthProvider: err.oauthProvider ?? "google",
          },
        });
        return;
      }
      // Email reservado (User soft-deleted con ese email): NO redirigir,
      // el user no puede registrarse de nuevo. Mostrar mensaje de soporte.
      if (err instanceof ApiError && err.code === "email_reserved") {
        setFormErr(t.auth.emailReserved);
        return;
      }
      const code = err instanceof ApiError ? err.code : "network_error";
      setFormErr(translateError(code));
    } finally {
      setSubmitting(false);
    }
  }

  function onGoogleError(code: string) {
    setFormErr(translateError(code));
  }

  async function onAppleIdToken(
    idToken: string,
    name?: { firstName: string; lastName: string },
  ) {
    setFormErr(null);
    setSubmitting(true);
    try {
      await appleSignIn(idToken, name);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // S14.3: un user NUEVO vía Apple sin consentimiento → backend devuelve
      // terms_not_accepted + oauth_pending → encaminamos a /register
      // ("completar registro") preservando id_token + name (Apple solo da el
      // name en el primer tap, hay que conservarlo para el reenvío).
      if (err instanceof ApiError && err.oauthPending) {
        navigate("/register", {
          state: {
            oauthIdToken: idToken,
            oauthEmail: err.oauthEmail,
            oauthName: err.oauthName,
            oauthProvider: err.oauthProvider ?? "apple",
            oauthAppleName: name,
          },
        });
        return;
      }
      if (err instanceof ApiError && err.code === "email_reserved") {
        setFormErr(t.auth.emailReserved);
        return;
      }
      const code = err instanceof ApiError ? err.code : "network_error";
      setFormErr(translateError(code));
    } finally {
      setSubmitting(false);
    }
  }

  function onAppleError(code: string) {
    setFormErr(translateError(code));
  }

  return (
    <AuthLayout
      title={t.auth.loginTitle}
      footer={
        <Link to="/reset-password" className="hover:text-bone">
          {t.auth.toReset}
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        {successMessage && (
          <p
            role="status"
            className="rounded-md border border-border-subtle bg-carbon-raised px-3 py-2.5 text-sm text-bone"
          >
            {successMessage}
          </p>
        )}

        {/* F3 (pre-flight): CTA PRIMARIO "Crear cuenta" para recién llegados.
            La beta está gated por invite, así que la mayoría que aterriza aquí
            viene a registrarse. El login (social + email) queda debajo como
            flujo secundario para quien ya tiene cuenta. Sin cambios de routing. */}
        <section
          data-testid="login-register-cta"
          className="flex flex-col gap-3 rounded-lg border border-lime/40 bg-lime/5 p-4"
        >
          <p className="text-sm text-bone">{t.auth.loginNewHerePrompt}</p>
          <Link
            to="/register"
            data-testid="login-create-account-btn"
            className="self-start rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
          >
            {t.auth.registerCta}
          </Link>
        </section>

        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.auth.loginAlreadyHaveAccount}
        </p>

        {/* Apple PRIMERO (guideline Apple: igual o más prominente que otros sociales). */}
        <AppleSignInButton onIdToken={onAppleIdToken} onError={onAppleError} />
        <GoogleSignInButton onIdToken={onGoogleIdToken} onError={onGoogleError} />

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <FormError message={formErr} />
          <Input
            label={t.auth.emailLabel}
            type="email"
            autoComplete="email"
            inputMode="email"
            autoFocus
            required
            placeholder={t.auth.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailErr}
          />
          <Input
            label={t.auth.passwordLabel}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={passErr}
          />
          <Button type="submit" loading={submitting} className="mt-2">
            {t.auth.loginCta}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
